import type { FC } from 'react';
import React, { memo, useCallback } from 'react';
import { Box } from '@withjoy/joykit';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useGetFilledPhotos } from '../../hooks/useGetFilledPhotos';
import type { Photo, PhotoTrioProps } from './PhotoTrio.types';
import { SsrLazyScreen } from '@apps/guest/packages/layout-engine/components/SsrLazyScreen';
import { pxToRem } from '@withjoy/joykit/theme';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { BrannanPhotoSlider } from '../PhotoSlider';
import { parseAspectRatio } from '@shared/utils/parseAspectRatio';
import { LOW_RES_RENDITION } from '@apps/guest/packages/layout-engine/components/OptimizedEventGuestSitePhoto';
import { addRendition } from '@shared/utils/photoRendition';
import { useIsStaysRedesignActive } from '../Honeymoon/useIsStaysRedesignActive';
import { ASPECT_RATIO, GAP } from './PhotoTrio.constants';

const PHOTO_COUNT_TO_SHOW = 3;
const HONEYMOON_TILE_CROSSFADE_DELAY_MS = 50;

export const PhotoTrio: FC<PhotoTrioProps> = memo(props => {
  const { photos, showSkeleton = false, showPlaceholder, container, photoRenderer: PhotoRenderer, alwaysShowTrio = false } = props;

  const [isMobileResponsive] = useResponsive({ values: { xxs: true, xs: false } });
  const showSlider = isMobileResponsive && !alwaysShowTrio;
  const isHoneymoonContext = useIsStaysRedesignActive();

  const { filledPhotos } = useGetFilledPhotos(photos, PHOTO_COUNT_TO_SHOW);

  const [containerDimensions, containerRef] = useSizeObserver();

  const getPhotoComponent = useCallback(
    (photo: Photo) => {
      const photoWidth = (containerDimensions.width - GAP * (PHOTO_COUNT_TO_SHOW - 1)) / PHOTO_COUNT_TO_SHOW;
      return (
        <Box
          width="100%"
          aspectRatio={ASPECT_RATIO}
        >
          <PhotoRenderer
            key={photo.id}
            photoUrl={photo.photoUrl}
            cropInfo={photo.cropInfo ? { ...photo.cropInfo, unit: '%' } : null}
            width={photoWidth}
            height={photoWidth / parseAspectRatio(ASPECT_RATIO)}
            containerType="fixed-aspect"
            crossfadeDelayMs={isHoneymoonContext ? HONEYMOON_TILE_CROSSFADE_DELAY_MS : undefined}
          />
        </Box>
      );
    },
    [containerDimensions.width, PhotoRenderer, isHoneymoonContext],
  );

  if (!showPlaceholder && photos.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        display="grid"
        ref={containerRef}
        gridTemplateColumns={{ _: 'repeat(1, minmax(0, 1fr))', xs: 'repeat(3, minmax(0, 1fr))' }}
        gap={pxToRem(GAP)}
        width={container === 'viewport' ? '100vw' : '100%'}
        position="relative"
        left={container === 'viewport' ? 'calc(-50vw + 50%)' : '0'}
        overflow="hidden"
      >
        {showSlider ? (
          <BrannanPhotoSlider
            photos={filledPhotos}
            showPlaceholder={showPlaceholder}
            aspectRatio={parseAspectRatio(ASPECT_RATIO)}
            photoRenderer={PhotoRenderer}
            crossfadeDelayMs={isHoneymoonContext ? HONEYMOON_TILE_CROSSFADE_DELAY_MS : undefined}
          />
        ) : (
          // PHOTO TRIO render logic
          <>
            {filledPhotos.map(photo => (
              <SsrLazyScreen
                key={photo.id}
                src={addRendition({ url: photo.photoUrl, renditionSize: LOW_RES_RENDITION })}
                width="100%"
                height="auto"
                aspectRatio={ASPECT_RATIO}
                showSkeleton={showSkeleton}
              >
                {() =>
                  photo.photoUrl ? (
                    getPhotoComponent(photo)
                  ) : (
                    <Box
                      width="100%"
                      height="auto"
                      aspectRatio={ASPECT_RATIO}
                      backgroundColor="lightgray"
                      key={photo.id}
                    />
                  )
                }
              </SsrLazyScreen>
            ))}
          </>
        )}
      </Box>
    </>
  );
});

PhotoTrio.displayName = 'PhotoTrio';
